class FrequentlyBoughtTogether extends HTMLElement {
  constructor() {
    super();
    this.button = this.querySelector("[data-fbt-add-to-cart]");
    this.buttonLabelTemplate =
      this.button?.dataset.fbtButtonLabel || "Add all [count] to cart";
    this.initiallyUnavailable = this.button?.disabled ?? false;

    this.querySelectorAll("[data-fbt-checkbox]").forEach((checkbox) => {
      checkbox.addEventListener("change", () => this.onCheckboxChange(checkbox));
      checkbox.addEventListener("click", (event) => event.stopPropagation());
    });

    this.querySelectorAll("[data-fbt-variant-select]").forEach((select) => {
      select.addEventListener("change", () => this.onVariantSelectChange(select));
    });

    this._onVariantChange = (event) => this.onMainVariantChange(event);
    document.addEventListener("variant:change", this._onVariantChange);

    if (this.button) {
      this.button.addEventListener("click", () => this.addToCart());
    }
  }

  disconnectedCallback() {
    document.removeEventListener("variant:change", this._onVariantChange);
  }

  onCheckboxChange(checkbox) {
    const item = checkbox.closest("[data-fbt-item]");
    if (item) {
      item.setAttribute(
        "data-fbt-selected",
        checkbox.checked ? "true" : "false"
      );
    }
    this.updateTotals();
  }

  onVariantSelectChange(select) {
    const item = select.closest("[data-fbt-item]");
    if (!item) return;

    const option = select.selectedOptions[0];
    if (!option) return;

    this.updateItemImage(item, option.dataset.variantImg);
    this.updateItemAvailability(item, !option.disabled);
    this.updateTotals();
  }

  onMainVariantChange(event) {
    const currentItem = this.querySelector("[data-fbt-current-product]");
    if (!currentItem) return;

    const sectionId = currentItem.dataset.sectionId;
    if (sectionId && event.detail?.sectionId && sectionId !== event.detail.sectionId) {
      return;
    }

    const variant = event.detail?.variant;
    if (!variant) return;

    const input = currentItem.querySelector("[data-fbt-variant-input]");
    if (input) {
      input.value = variant.id;
      input.dataset.price = variant.price;
      const comparePrice =
        variant.compare_at_price && variant.compare_at_price > variant.price
          ? variant.compare_at_price
          : variant.price;
      input.dataset.comparePrice = comparePrice;
    }

    const variantImage =
      variant.featured_image?.src ||
      variant.featured_media?.preview_image?.src;
    this.updateItemImage(currentItem, variantImage);
    this.updateItemAvailability(currentItem, variant.available !== false);
    this.updateTotals();
  }

  updateItemImage(item, imageSrc) {
    if (!imageSrc) return;

    const img = item.querySelector(".fbt__image");
    if (img) {
      img.src = imageSrc;
      img.removeAttribute("srcset");
    }
  }

  updateItemAvailability(item, isAvailable) {
    const checkbox = item.querySelector("[data-fbt-checkbox]");
    if (checkbox) {
      checkbox.disabled = !isAvailable;
    }
  }

  isItemSelected(item) {
    if (item.hasAttribute("data-fbt-required")) return true;
    const checkbox = item.querySelector("[data-fbt-checkbox]");
    return checkbox ? checkbox.checked : true;
  }

  getVariantDataFromItem(item) {
    const select = item.querySelector("[data-fbt-variant-select]");
    if (select) {
      const option = select.selectedOptions[0];
      if (!option?.value) return null;
      return {
        id: Number(option.value),
        quantity: 1,
        price: Number(option.dataset.price) || 0,
        comparePrice: Number(option.dataset.comparePrice) || 0,
        available: !option.disabled,
      };
    }

    const input = item.querySelector("[data-fbt-variant-input]");
    if (!input?.value) return null;

    return {
      id: Number(input.value),
      quantity: 1,
      price: Number(input.dataset.price) || 0,
      comparePrice: Number(input.dataset.comparePrice) || 0,
      available: true,
    };
  }

  getSelectedItemsData() {
    const items = [];
    this.querySelectorAll("[data-fbt-item]").forEach((item) => {
      if (!this.isItemSelected(item)) return;

      const variantData = this.getVariantDataFromItem(item);
      if (variantData) {
        items.push(variantData);
      }
    });
    return items;
  }

  getItems() {
    return this.getSelectedItemsData().map(({ id, quantity }) => ({
      id,
      quantity,
    }));
  }

  formatMoney(amount) {
    if (!window.Shopify?.formatMoney) return String(amount);
    const formatted = window.Shopify.formatMoney(amount);
    const tmp = document.createElement("div");
    tmp.innerHTML = formatted;
    return (tmp.textContent || tmp.innerText || "").replace(".00", "");
  }

  getItemDisplayPrices(basePrice, compareAtPrice, totalPrice) {
    const enableDiscount = this.dataset.enableDiscount === "true";
    let salePrice = basePrice;
    let displayCompare = 0;
    let showStrikethrough = false;

    if (enableDiscount && totalPrice > 0 && basePrice > 0) {
      displayCompare = basePrice;

      if (this.dataset.discountType === "percentage") {
        const discountFactor =
          100 - (Number(this.dataset.discountPercentage) || 0);
        salePrice = Math.floor((basePrice * discountFactor) / 100);
      } else {
        const discountFixed = Number(this.dataset.discountFixed) || 0;
        const itemSavings = Math.floor(
          (discountFixed * basePrice) / totalPrice
        );
        salePrice = Math.max(0, basePrice - itemSavings);
      }

      showStrikethrough = salePrice < displayCompare;
    } else if (compareAtPrice > basePrice) {
      displayCompare = compareAtPrice;
      showStrikethrough = true;
    }

    return { salePrice, displayCompare, showStrikethrough };
  }

  updateItemPriceDisplay(item, basePrice, compareAtPrice, totalPrice) {
    const priceEl = item.querySelector(".fbt__product-price");
    if (!priceEl) return;

    const { salePrice, displayCompare, showStrikethrough } =
      this.getItemDisplayPrices(basePrice, compareAtPrice, totalPrice);

    if (showStrikethrough) {
      priceEl.innerHTML = `
        <span class="visually-hidden">Regular price</span>
        <s class="fbt__product-compare">${this.formatMoney(displayCompare)}</s>
        <span class="visually-hidden">Sale price</span>
        <span class="fbt__product-sale">${this.formatMoney(salePrice)}</span>
      `;
    } else {
      priceEl.textContent = this.formatMoney(salePrice);
    }
  }

  updateTotals() {
    const selectedItems = this.getSelectedItemsData();
    let totalPrice = 0;
    let totalComparePrice = 0;
    let allAvailable = true;

    selectedItems.forEach((item) => {
      totalPrice += item.price;
      totalComparePrice += item.comparePrice;
      if (item.available === false) {
        allAvailable = false;
      }
    });

    this.querySelectorAll("[data-fbt-item]").forEach((item) => {
      if (!this.isItemSelected(item)) return;
      const variantData = this.getVariantDataFromItem(item);
      if (!variantData) return;
      this.updateItemPriceDisplay(
        item,
        variantData.price,
        variantData.comparePrice,
        totalPrice
      );
    });

    const enableDiscount = this.dataset.enableDiscount === "true";
    const discountType = this.dataset.discountType || "percentage";
    const discountPercentage = Number(this.dataset.discountPercentage) || 0;
    const discountFixed = Number(this.dataset.discountFixed) || 0;
    const showSavings = this.dataset.showSavings === "true";
    const savingsLabel = this.dataset.savingsLabel || "Save [amount]";
    const compareSavingsLabel =
      this.dataset.compareSavingsLabel || "You save: [amount]";

    let savingsAmount = 0;
    let discountedTotal = totalPrice;
    let footerCompareTotal = totalPrice;
    let showFooterTotals = totalPrice > 0;
    let showFooterCompare = false;

    if (enableDiscount && totalPrice > 0) {
      if (discountType === "fixed") {
        savingsAmount = discountFixed;
      } else {
        savingsAmount = Math.floor((totalPrice * discountPercentage) / 100);
      }
      discountedTotal = Math.max(0, totalPrice - savingsAmount);
      if (savingsAmount > 0) {
        footerCompareTotal = totalPrice;
        showFooterCompare = true;
      }
    } else if (totalComparePrice > totalPrice) {
      savingsAmount = totalComparePrice - totalPrice;
      discountedTotal = totalPrice;
      footerCompareTotal = totalComparePrice;
      showFooterCompare = true;
    }

    const totalsEl = this.querySelector(".fbt__totals");
    if (totalsEl) {
      totalsEl.hidden = !showFooterTotals;
    }

    const compareEl = this.querySelector(".fbt__compare-total");
    if (compareEl) {
      compareEl.textContent = this.formatMoney(footerCompareTotal);
      compareEl.hidden = !showFooterCompare;
    }

    const discountedEl = this.querySelector("[data-fbt-discounted-total]");
    if (discountedEl) {
      discountedEl.textContent = this.formatMoney(discountedTotal);
    }

    const savingsEls = this.querySelectorAll("[data-fbt-savings]");
    savingsEls.forEach((el) => {
      if (!showFooterTotals || savingsAmount <= 0) {
        el.hidden = true;
        return;
      }

      el.hidden = false;
      if (enableDiscount && showSavings) {
        el.textContent = savingsLabel.replace(
          "[amount]",
          this.formatMoney(savingsAmount)
        );
      } else if (!enableDiscount) {
        el.textContent = compareSavingsLabel.replace(
          "[amount]",
          this.formatMoney(savingsAmount)
        );
      }
    });

    const buttonTextEl = this.querySelector("[data-fbt-button-text]");
    if (buttonTextEl) {
      buttonTextEl.textContent = this.buttonLabelTemplate.replace(
        "[count]",
        selectedItems.length
      );
    }

    if (this.button) {
      this.button.disabled =
        this.initiallyUnavailable || selectedItems.length === 0 || !allAvailable;
    }
  }

  async addToCart() {
    const items = this.getItems();
    if (!items.length || !this.button) return;

    this.button.classList.add("loading");
    this.button.disabled = true;
    this.button.setAttribute("aria-busy", "true");
    this.button.querySelector(".loading__spinner")?.classList.remove("hidden");

    try {
      const cart =
        document.querySelector("cart-notification") ||
        document.querySelector("cart-drawer");

      let sections = [];
      if (cart) {
        sections = cart.getSectionsToRender().map((section) => section.id);
      } else if (window.themeSettings?.cart?.type === "page") {
        sections = ["cart-icon-bubble"];
      }

      const formData = new FormData();
      items.forEach((item, i) => {
        formData.append(`items[${i}][id]`, item.id);
        formData.append(`items[${i}][quantity]`, item.quantity);
      });
      if (sections.length) formData.append("sections", sections.join(","));
      formData.append("sections_url", window.location.pathname);

      const response = await fetch(`${window.Shopify.routes.root}cart/add.js`, {
        method: "POST",
        headers: { "X-Requested-With": "XMLHttpRequest" },
        body: formData,
      });

      if (!response.ok) {
        throw new Error("Network response was not ok");
      }

      const responseData = await response.json();

      if (cart) {
        cart.renderContents(responseData);
        if (cart.classList.contains("is-empty")) {
          cart.classList.remove("is-empty");
        }
      } else if (window.themeSettings?.cart?.auto_open_page) {
        window.location.href = `${window.Shopify.routes.root}cart`;
      } else if (responseData.sections?.["cart-icon-bubble"]) {
        const cartIconBubble = document.getElementById("cart-icon-bubble");
        if (cartIconBubble) {
          cartIconBubble.innerHTML = new DOMParser()
            .parseFromString(
              responseData.sections["cart-icon-bubble"],
              "text/html"
            )
            .querySelector(".shopify-section").innerHTML;
        }
      }
    } catch (error) {
      console.error("Error adding bundle to cart:", error);
      if (window.themeSettings?.cart?.auto_open_page) {
        window.location.href = `${window.Shopify.routes.root}cart`;
      }
    } finally {
      this.button.classList.remove("loading");
      this.updateTotals();
      this.button.setAttribute("aria-busy", "false");
      this.button.querySelector(".loading__spinner")?.classList.add("hidden");
    }
  }
}

customElements.define("frequently-bought-together", FrequentlyBoughtTogether);
